import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SECTION_ACTION_CLASS, SectionHeader } from '@knitknot/shared/components/ui/section-header';
import { Skeleton } from '@knitknot/shared/components/ui/skeleton';
import { useEntityNav } from '@/lib/useEntityNav';
import { consoleRoutes } from '@/lib/routes';
import { type PlaybookListItem } from '@/types/playbooks';

/* ────────────────────────────────────────────────────────────────────────────
   NextActionsCard — the top actionable playbooks (row 3, right). Keeps the
   one compact prioritized-actions presence on home ("no next steps" is the #1
   reviewer criticism of the category). Capped dashboard widget — static
   columns, click-through to the playbook detail.
   ──────────────────────────────────────────────────────────────────────────── */

/** Match the issue queue beside it: Overview is a decision surface, not the
 *  complete work inventory. */
const MAX_ROWS = 5;

export function NextActionsCard({
  playbooks,
  loading,
}: {
  /** Actionable playbooks (proposed/approved/in_progress), importance-ordered. */
  playbooks: PlaybookListItem[];
  loading: boolean;
}) {
  const nav = useEntityNav();
  const rows = playbooks.slice(0, MAX_ROWS);

  return (
    <section className="flex h-full min-h-0 flex-col">
      <SectionHeader
        title="Playbooks"
        action={
          <Link to={consoleRoutes.publish.playbooks} className={SECTION_ACTION_CLASS} aria-label="View all playbooks">
            View all
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        }
      />
      <div className="mt-2 flex min-h-0 flex-1 flex-col">
        {rows.length > 0 ? (
          <ol>
            {rows.map((row, index) => (
              <li key={row.id}>
                <Link
                  to={nav.playbook(row.id)}
                  className="focus-ring group grid grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-3 rounded-sm py-3"
                >
                  <span className="pt-0.5 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
                  {/* Title only (Max, 2026-09-04). The sub-line read
                      "{status} · customer-managed" and neither half earned its
                      row: this rail lists ACTIVE plays by construction, so the
                      status was near-constant chrome, and "customer-managed" is
                      true of every play we have ever shipped — a constant
                      printed five times. Calm-pass rule 4: a value identical
                      across the full row set is chrome, not information. */}
                  <span className="block min-w-0 truncate text-sm font-medium text-foreground group-hover:underline">
                    {row.title}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        ) : loading ? (
          <Skeleton className="h-40" aria-label="Loading playbooks" />
        ) : (
          <p className="px-2 py-2.5 text-sm text-muted-foreground">No active playbooks</p>
        )}
      </div>
    </section>
  );
}

export default NextActionsCard;
